'use client';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useDebug } from '@/utils/useDebugLog';
const { log } = useDebug({ name: 'ScrollRestorationManager', bgColor: 'brown', textColor: 'white', enabled: false });

interface ScrollPosition {
  x: number;
  y: number;
  timestamp: number;
}

type ScrollPositions = Record<string, ScrollPosition>;

export function ScrollRestorationManager(): null {
  const router = useRouter();
  const { asPath } = router;
  const isNavigatingRef = useRef(false);
  const navigationTypeRef = useRef<
    'push' | 'replace' | 'back' | 'forward' | null
  >(null);
  const lastAsPathRef = useRef(asPath);

  // Routes where scroll restoration should be disabled
  const excludedRoutes = ['/inductees/'];

  const isExcludedRoute = (path: string) => {
    const excluded = excludedRoutes.some((route) => path.startsWith(route));
    log(`Checking if ${path} is excluded: ${excluded}`);
    return excluded;
  };

  useEffect(() => {
    log('Initializing scroll restoration manager');

    const trackNavigationType = () => {
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;

      history.pushState = function (...args) {
        navigationTypeRef.current = 'push';
        isNavigatingRef.current = true;
        log('Navigation type: push');
        return originalPushState.apply(this, args);
      };

      const handlePopState = () => {
        navigationTypeRef.current = 'back';
        isNavigatingRef.current = true;
        log('Navigation type: back (popstate)');
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
        window.removeEventListener('popstate', handlePopState);
        log('Cleaned up navigation tracking');
      };
    };

    const cleanup = trackNavigationType();

    const saveScrollPosition = () => {
      if (typeof window === 'undefined') return;

      const currentPath = lastAsPathRef.current;
      log(`Attempting to save scroll position for: ${currentPath}`);

      // Skip saving scroll position for excluded routes
      if (isExcludedRoute(currentPath)) {
        log(`Skipping save for excluded route: ${currentPath}`);
        return;
      }

      const positions: ScrollPositions = JSON.parse(
        sessionStorage.getItem('scrollPositions') || '{}'
      );

      const scrollPosition = {
        x: window.scrollX,
        y: window.scrollY,
        timestamp: Date.now(),
      };

      positions[currentPath] = scrollPosition;
      log(`Saved scroll position for ${currentPath}:`, scrollPosition);

      sessionStorage.setItem('scrollPositions', JSON.stringify(positions));
    };

    const handleRouteChange = (url: string) => {
      const navigationType = navigationTypeRef.current;
      const sectionId = router.asPath.split('#')[1];

      log(`Route change to: ${url}, navigation type: ${navigationType}, section: ${sectionId || 'none'}`);

      setTimeout(() => {
        isNavigatingRef.current = false;
        navigationTypeRef.current = null;
      }, 100);

      // Always scroll to top for excluded routes
      if (isExcludedRoute(url) || sectionId) {
        log(`Scrolling to top for ${url} (excluded route or has section)`);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      } else if (navigationType === 'back' || navigationType === 'forward') {
        log(`Restoring scroll position for back/forward navigation to: ${url}`);
        restoreScrollPosition(url);
      } else {
        log(`Scrolling to top for new navigation to: ${url}`);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }

      lastAsPathRef.current = url;
    };

    const restoreScrollPosition = (url: string) => {
      const positions: ScrollPositions = JSON.parse(
        sessionStorage.getItem('scrollPositions') || '{}'
      );
      const savedPosition = positions[url];

      if (savedPosition) {
        log(`Restoring scroll position for ${url}:`, savedPosition);
        requestAnimationFrame(() => {
          window.scrollTo(savedPosition.x, savedPosition.y);
        });
      } else {
        log(`No saved position found for ${url}, scrolling to top`);
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    };

    const handleRouteChangeStart = () => {
      log('Route change starting, saving current position');
      saveScrollPosition();
    };

    const handleBeforeUnload = () => {
      log('Page unloading, saving current position');
      saveScrollPosition();
    };

    const cleanupOldPositions = () => {
      const positions: ScrollPositions = JSON.parse(
        sessionStorage.getItem('scrollPositions') || '{}'
      );
      const oneHourAgo = Date.now() - 60 * 60 * 1000;

      const cleanedPositions: ScrollPositions = {};
      let removedCount = 0;

      Object.entries(positions).forEach(([path, position]) => {
        if (position.timestamp > oneHourAgo) {
          cleanedPositions[path] = position;
        } else {
          removedCount++;
        }
      });

      log(`Cleaned up ${removedCount} old scroll positions`);

      sessionStorage.setItem(
        'scrollPositions',
        JSON.stringify(cleanedPositions)
      );
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    cleanupOldPositions();

    return () => {
      cleanup();
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      log('Scroll restoration manager cleanup complete');
    };
  }, [router]);

  useEffect(() => {
    log(`asPath updated to: ${asPath}`);
    lastAsPathRef.current = asPath;
  }, [asPath]);

  return null;
}
